import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faHome, faBox } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import { Button, Container, Section } from '../styles/components';
import PageHero from '../components/layout/PageHero';

const PageContainer = styled.div`
  padding-bottom: ${theme.spacing.xl};
`;

const SuccessIcon = styled.div`
  color: ${theme.colors.success};
  font-size: 5rem;
  margin-bottom: ${theme.spacing.lg};
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.fonts.heading};
`;

const Message = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.darkGray};
  max-width: 600px;
  margin: 0 auto ${theme.spacing.lg};
`;

const OrderDetails = styled.div`
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.small};
  width: 100%;
  max-width: 600px;
  margin-bottom: ${theme.spacing.xl};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.lightGray};
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const OrderSuccessPage: React.FC = () => {
  // Generate random order ID
  const orderId = `ALH-${Math.floor(100000 + Math.random() * 900000)}`;
  const orderDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <PageContainer>
      <PageHero 
        title="Order Successful"
        subtitle="Thank you for your purchase"
        height="30vh"
      />
      <Section>
        <Container>
          <div className="text-center">
            <SuccessIcon>
              <FontAwesomeIcon icon={faCheckCircle} />
            </SuccessIcon>
            
            <PageTitle>Thank You For Your Order!</PageTitle>
            
            <Message>
              Your order has been successfully placed. We've sent a confirmation email
              with all the details. You'll receive another notification when your order ships.
            </Message>
            
            <OrderDetails>
              <DetailRow>
                <DetailLabel>Order Number:</DetailLabel>
                <span>{orderId}</span>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Date:</DetailLabel>
                <span>{orderDate}</span>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Payment Method:</DetailLabel>
                <span>Credit Card</span>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Shipping Method:</DetailLabel>
                <span>Standard Delivery (1-3 days)</span>
              </DetailRow>
            </OrderDetails>
            
            <ButtonsContainer>
              <Button as={Link} to="/" variant="outline">
                <FontAwesomeIcon icon={faHome} /> Return to Home
              </Button>
              <Button as={Link} to="/products" variant="primary">
                <FontAwesomeIcon icon={faBox} /> Continue Shopping
              </Button>
            </ButtonsContainer>
          </div>
        </Container>
      </Section>
    </PageContainer>
  );
};

export default OrderSuccessPage; 